import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, pollJob, rateLimitMessage } from "@/lib/api";
import { X, Library, Globe2, Sparkles, Loader2, Check, ExternalLink, AlertCircle, Building2, Zap, StopCircle } from "lucide-react";

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${Number(n).toFixed(0)}`;
};

// Row-saturation modal. Triggered from MutualFitMatrix's "Saturate row" button.
// Fires BOTH /firms/{id}/find-partners (library-only, ranked w/ fit-score) AND
// /firms/{id}/suggest-partners (real companies NOT in DB) in parallel, then
// lets the user one-click score+save each suggestion.
//
// - Library matches → 1-click "Score & save" calls POST /api/synergy/best (a, b)
//   which auto-picks the best product + persists the synergy (becomes a matrix cell).
// - External suggestions → 2-step flow per row: POST /api/research → /api/firms (save),
//   then a follow-up POST /api/synergy/best to score it against the source firm.
//   Both steps run on a single button click; per-row state machine tracks progress.
export default function SaturateRowModal({ firm, scoredTargetIds, onClose, onSaved }) {
    const [libraryState, setLibraryState] = useState({ status: "loading", matches: [], error: "" });
    const [externalState, setExternalState] = useState({ status: "loading", suggestions: [], error: "" });
    const [rowStates, setRowStates] = useState({}); // key → { status, label, error, link }
    // Batch runner state — independent per section ("lib" / "ext").
    // { running: bool, total, done, current, aborted }
    const [batchLib, setBatchLib] = useState({ running: false, total: 0, done: 0, current: "", aborted: false });
    const [batchExt, setBatchExt] = useState({ running: false, total: 0, done: 0, current: "", aborted: false });
    // Abort signal — bumping this number tells in-flight loops to stop after the current row.
    const abortRef = useRef({ lib: 0, ext: 0 });

    // Kick off both flows in parallel on mount. Effect only depends on firm.id —
    // the scoredTargetIds prop is consumed at render time below (post-filter), so reloads
    // of the parent (after onSaved → reload()) do NOT refire these AI calls.
    useEffect(() => {
        let cancelled = false;

        // Library matches (ranked) — job-based
        (async () => {
            try {
                const { data: enqueued } = await api.post(`/firms/${firm.id}/find-partners`, { limit: 20 });
                const result = await pollJob(enqueued.job_id, { timeoutMs: 4 * 60 * 1000 });
                if (cancelled) return;
                setLibraryState({ status: "done", matches: result.matches || [], error: "" });
            } catch (e) {
                if (cancelled) return;
                const rl = rateLimitMessage(e);
                setLibraryState({ status: "error", matches: [], error: rl || e?.response?.data?.detail || e.message || "Library matcher failed." });
            }
        })();

        // External suggestions — sync endpoint
        (async () => {
            try {
                const { data } = await api.post(`/firms/${firm.id}/suggest-partners`);
                if (cancelled) return;
                setExternalState({ status: "done", suggestions: data.suggestions || [], error: "" });
            } catch (e) {
                if (cancelled) return;
                const rl = rateLimitMessage(e);
                setExternalState({ status: "error", suggestions: [], error: rl || e?.response?.data?.detail || e.message || "External suggester failed." });
            }
        })();

        return () => { cancelled = true; };
    }, [firm.id]);

    // Filter library matches at render time: already-scored targets are dropped from the list.
    // Live-updates as scoredTargetIds changes (e.g. after a Score & save adds a new synergy).
    const freshLibraryMatches = libraryState.matches.filter((m) => !scoredTargetIds.has(m.firm_b_id));

    const setRow = (key, patch) =>
        setRowStates((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

    // ---- Library row action: 1-click "Score & save". Returns true on success, false on failure. ----
    const scoreLibrary = async (match) => {
        const key = `lib:${match.firm_b_id}`;
        setRow(key, { status: "scoring", label: "Scoring…", error: "" });
        try {
            const { data: enq } = await api.post("/synergy/best", { firm_a_id: firm.id, firm_b_id: match.firm_b_id });
            const result = await pollJob(enq.job_id, { timeoutMs: 4 * 60 * 1000 });
            await api.post("/synergies/pin", {
                firm_a_id: result.firm_a.id,
                firm_b_id: result.firm_b.id,
                product_id: result.product.id,
                analysis: result,
            });
            setRow(key, {
                status: "done",
                label: `Saved · fit ${result.fit_score}`,
                link: `/app/synergy?key=${encodeURIComponent(`${result.firm_a.id}:${result.firm_b.id}:${result.product.id}`)}`,
            });
            onSaved && onSaved();
            return true;
        } catch (e) {
            const rl = rateLimitMessage(e);
            setRow(key, { status: "error", label: "", error: rl || e?.response?.data?.detail || e.message || "Failed" });
            return false;
        }
    };

    // ---- External row action: research → save firm → score → save synergy. Returns true/false. ----
    const researchAndScore = async (suggestion) => {
        const key = `ext:${suggestion.name}`;
        setRow(key, { status: "researching", label: "Researching…", error: "" });
        try {
            const { data: enqR } = await api.post("/research", { firm_name: suggestion.name });
            const dossier = await pollJob(enqR.job_id, { timeoutMs: 4 * 60 * 1000 });

            setRow(key, { status: "saving", label: "Adding to library…" });
            const { data: newFirm } = await api.post("/firms", dossier);

            setRow(key, { status: "scoring", label: "Scoring fit…" });
            const { data: enqS } = await api.post("/synergy/best", { firm_a_id: firm.id, firm_b_id: newFirm.id });
            const synResult = await pollJob(enqS.job_id, { timeoutMs: 4 * 60 * 1000 });

            await api.post("/synergies/pin", {
                firm_a_id: synResult.firm_a.id,
                firm_b_id: synResult.firm_b.id,
                product_id: synResult.product.id,
                analysis: synResult,
            });
            setRow(key, {
                status: "done",
                label: `Saved · fit ${synResult.fit_score}`,
                link: `/app/synergy?key=${encodeURIComponent(`${synResult.firm_a.id}:${synResult.firm_b.id}:${synResult.product.id}`)}`,
            });
            onSaved && onSaved();
            return true;
        } catch (e) {
            const rl = rateLimitMessage(e);
            setRow(key, { status: "error", label: "", error: rl || e?.response?.data?.detail || e.message || "Failed" });
            return false;
        }
    };

    // ---- Batch runners ----
    // Run scoreLibrary for the top N unscored library rows sequentially. User can stop mid-run.
    const runBatchLib = async (n) => {
        const queue = freshLibraryMatches
            .filter((m) => !["done", "scoring"].includes((rowStates[`lib:${m.firm_b_id}`] || {}).status))
            .slice(0, n);
        if (queue.length === 0) return;
        const token = abortRef.current.lib + 1;
        abortRef.current.lib = token;
        setBatchLib({ running: true, total: queue.length, done: 0, current: queue[0].firm_b_name, aborted: false });
        for (let i = 0; i < queue.length; i++) {
            if (abortRef.current.lib !== token) break;
            setBatchLib((s) => ({ ...s, done: i, current: queue[i].firm_b_name }));
            await scoreLibrary(queue[i]);
        }
        setBatchLib((s) => ({ ...s, running: false, done: s.total, current: "", aborted: abortRef.current.lib !== token }));
    };
    const stopBatchLib = () => {
        abortRef.current.lib += 1;
        setBatchLib((s) => ({ ...s, running: false, aborted: true }));
    };

    // Run researchAndScore for the top N external suggestions sequentially.
    const runBatchExt = async (n) => {
        const queue = externalState.suggestions
            .filter((s) => !["done", "researching", "saving", "scoring"].includes((rowStates[`ext:${s.name}`] || {}).status))
            .slice(0, n);
        if (queue.length === 0) return;
        const token = abortRef.current.ext + 1;
        abortRef.current.ext = token;
        setBatchExt({ running: true, total: queue.length, done: 0, current: queue[0].name, aborted: false });
        for (let i = 0; i < queue.length; i++) {
            if (abortRef.current.ext !== token) break;
            setBatchExt((s) => ({ ...s, done: i, current: queue[i].name }));
            await researchAndScore(queue[i]);
        }
        setBatchExt((s) => ({ ...s, running: false, done: s.total, current: "", aborted: abortRef.current.ext !== token }));
    };
    const stopBatchExt = () => {
        abortRef.current.ext += 1;
        setBatchExt((s) => ({ ...s, running: false, aborted: true }));
    };

    const libraryReady = libraryState.status === "done";
    const externalReady = externalState.status === "done";

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="saturate-row-modal">
            <div className="border border-zinc-800 bg-zinc-950 w-full max-w-5xl my-8">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4 sticky top-0 bg-zinc-950 z-10">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-1">// Saturate row</div>
                        <h2 className="font-heading font-black text-2xl text-zinc-50 leading-tight">
                            Find partners for <span className="text-amber-500">{firm.name}</span>
                        </h2>
                        <p className="text-xs text-zinc-500 mt-1 font-mono">
                            From your library AND from companies not in your database.
                        </p>
                    </div>
                    <button onClick={onClose} data-testid="saturate-close" className="text-zinc-500 hover:text-amber-500 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-800">
                    {/* LIBRARY MATCHES */}
                    <section className="bg-zinc-950 p-5" data-testid="saturate-library-section">
                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                            <Library className="w-4 h-4 text-emerald-500" />
                            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-400">From your library</div>
                            <span className="font-mono text-[10px] text-zinc-600">
                                {libraryReady ? `${freshLibraryMatches.length} unscored` : ""}
                            </span>
                            {libraryReady && freshLibraryMatches.length > 1 && (
                                <BatchControls
                                    section="lib"
                                    available={freshLibraryMatches.length}
                                    state={batchLib}
                                    onRun={runBatchLib}
                                    onStop={stopBatchLib}
                                    accent="emerald"
                                />
                            )}
                        </div>
                        {batchLib.running && (
                            <BatchProgress state={batchLib} accent="emerald" testid="batch-lib-progress" />
                        )}
                        {libraryState.status === "loading" && (
                            <div className="flex flex-col items-center justify-center py-10 gap-3" data-testid="saturate-library-loading">
                                <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                    Pair-scoring your library…
                                </div>
                            </div>
                        )}
                        {libraryState.status === "error" && (
                            <div className="border border-red-900/60 bg-red-950/30 text-red-400 text-xs px-3 py-2 font-mono">
                                {libraryState.error}
                            </div>
                        )}
                        {libraryReady && freshLibraryMatches.length === 0 && (
                            <div className="border border-dashed border-zinc-800 p-6 text-center">
                                <div className="font-mono text-xs text-zinc-500">Every library firm is already scored.</div>
                            </div>
                        )}
                        {libraryReady && freshLibraryMatches.length > 0 && (
                            <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                                {freshLibraryMatches.map((m) => {
                                    const key = `lib:${m.firm_b_id}`;
                                    const state = rowStates[key] || {};
                                    return (
                                        <li
                                            key={m.firm_b_id}
                                            className="border border-zinc-800 bg-black/40 p-3 flex items-start gap-3"
                                            data-testid={`saturate-library-row-${m.firm_b_id}`}
                                        >
                                            <FitBadge score={m.fit_score} />
                                            <div className="min-w-0 flex-1">
                                                <div className="font-heading font-bold text-zinc-100 text-sm truncate">{m.firm_b_name}</div>
                                                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
                                                    {m.firm_b_industry || "—"} · {m.firm_b_region || "—"}
                                                </div>
                                                {m.rationale && (
                                                    <div className="text-xs text-zinc-400 mt-1 line-clamp-2 leading-snug">{m.rationale}</div>
                                                )}
                                                {state.error && (
                                                    <div className="text-[10px] text-red-400 font-mono mt-1 flex items-center gap-1">
                                                        <AlertCircle className="w-3 h-3" /> {state.error}
                                                    </div>
                                                )}
                                            </div>
                                            <RowAction
                                                state={state}
                                                onRun={() => scoreLibrary(m)}
                                                labelIdle="Score & save"
                                                testId={`saturate-library-action-${m.firm_b_id}`}
                                            />
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>

                    {/* EXTERNAL SUGGESTIONS */}
                    <section className="bg-zinc-950 p-5" data-testid="saturate-external-section">
                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                            <Globe2 className="w-4 h-4 text-amber-500" />
                            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-400">Not in your database</div>
                            <span className="font-mono text-[10px] text-zinc-600">
                                {externalReady ? `${externalState.suggestions.length} suggested` : ""}
                            </span>
                            {externalReady && externalState.suggestions.length > 1 && (
                                <BatchControls
                                    section="ext"
                                    available={externalState.suggestions.length}
                                    state={batchExt}
                                    onRun={runBatchExt}
                                    onStop={stopBatchExt}
                                    accent="amber"
                                    costPerRow={3}
                                />
                            )}
                        </div>
                        {batchExt.running && (
                            <BatchProgress state={batchExt} accent="amber" testid="batch-ext-progress" />
                        )}
                        {externalState.status === "loading" && (
                            <div className="flex flex-col items-center justify-center py-10 gap-3" data-testid="saturate-external-loading">
                                <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                    Hunting real companies…
                                </div>
                            </div>
                        )}
                        {externalState.status === "error" && (
                            <div className="border border-red-900/60 bg-red-950/30 text-red-400 text-xs px-3 py-2 font-mono">
                                {externalState.error}
                            </div>
                        )}
                        {externalReady && externalState.suggestions.length === 0 && (
                            <div className="border border-dashed border-zinc-800 p-6 text-center">
                                <div className="font-mono text-xs text-zinc-500">No external suggestions returned.</div>
                            </div>
                        )}
                        {externalReady && externalState.suggestions.length > 0 && (
                            <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                                {externalState.suggestions.map((s, i) => {
                                    const key = `ext:${s.name}`;
                                    const state = rowStates[key] || {};
                                    return (
                                        <li
                                            key={`${s.name}-${i}`}
                                            className="border border-zinc-800 bg-black/40 p-3 flex items-start gap-3"
                                            data-testid={`saturate-external-row-${i}`}
                                        >
                                            <div className="w-10 h-10 border border-amber-500/40 bg-amber-500/5 flex items-center justify-center shrink-0">
                                                <Building2 className="w-4 h-4 text-amber-500" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="font-heading font-bold text-zinc-100 text-sm truncate">{s.name}</div>
                                                    {s.category && (
                                                        <span className="font-mono text-[9px] uppercase tracking-wider border border-zinc-700 text-zinc-400 px-1.5 py-0.5">{s.category}</span>
                                                    )}
                                                </div>
                                                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
                                                    {s.industry || "—"} · {s.region || "—"}
                                                </div>
                                                {s.reason && (
                                                    <div className="text-xs text-zinc-400 mt-1 line-clamp-2 leading-snug">{s.reason}</div>
                                                )}
                                                {state.error && (
                                                    <div className="text-[10px] text-red-400 font-mono mt-1 flex items-center gap-1">
                                                        <AlertCircle className="w-3 h-3" /> {state.error}
                                                    </div>
                                                )}
                                            </div>
                                            <RowAction
                                                state={state}
                                                onRun={() => researchAndScore(s)}
                                                labelIdle="Research & add"
                                                testId={`saturate-external-action-${i}`}
                                            />
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                </div>

                <div className="border-t border-zinc-800 px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-mono text-[10px] text-zinc-500">
                        <Sparkles className="inline w-3 h-3 mr-1 text-amber-500" />
                        Each "Research & add" call costs ~3 AI requests. Score & save costs 1.
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="saturate-done-btn"
                        className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-500 border border-zinc-800 hover:border-amber-500 px-3 py-1.5 transition-colors"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}

function FitBadge({ score }) {
    const tone =
        score >= 90 ? "text-emerald-300 bg-emerald-500/15 border-emerald-500/40"
        : score >= 80 ? "text-blue-300 bg-blue-500/15 border-blue-500/40"
        : score >= 70 ? "text-amber-300 bg-amber-500/15 border-amber-500/40"
        : "text-zinc-300 bg-zinc-800/50 border-zinc-700";
    return (
        <div className={`shrink-0 w-10 h-10 border flex items-center justify-center font-mono font-bold text-sm ${tone}`} title={`Predicted fit: ${score}`}>
            {score}
        </div>
    );
}

function RowAction({ state, onRun, labelIdle, testId }) {
    const status = state.status;
    if (status === "done") {
        return (
            <div className="shrink-0 flex flex-col items-end gap-1">
                <div className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-emerald-400">
                    <Check className="w-3 h-3" /> {state.label || "Saved"}
                </div>
                {state.link && (
                    <Link to={state.link} className="inline-flex items-center gap-0.5 font-mono text-[10px] text-amber-500 hover:text-amber-400" data-testid={`${testId}-open`}>
                        Open <ExternalLink className="w-3 h-3" />
                    </Link>
                )}
            </div>
        );
    }
    if (status && status !== "error") {
        return (
            <div className="shrink-0 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                {state.label || "Working…"}
            </div>
        );
    }
    return (
        <button
            type="button"
            onClick={onRun}
            data-testid={testId}
            className="shrink-0 inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold text-[10px] uppercase tracking-wider px-3 py-2 transition-colors"
        >
            <Sparkles className="w-3 h-3" /> {labelIdle}
        </button>
    );
}

// Batch controls — a small N selector + Run/Stop button, used at the top of each section.
// `available` caps the N slider so users can't ask for more than exists.
// `costPerRow` is a UX hint (1 for library / 3 for external) used in the cost preview.
function BatchControls({ section, available, state, onRun, onStop, accent, costPerRow = 1 }) {
    const [n, setN] = useState(Math.min(5, available));
    const cap = Math.min(10, available); // hard ceiling so users don't accidentally burn the whole budget
    useEffect(() => { setN((cur) => Math.min(cur, available, 10)); }, [available]);

    if (state.running) {
        return (
            <button
                type="button"
                onClick={onStop}
                data-testid={`batch-${section}-stop`}
                className="ml-auto inline-flex items-center gap-1.5 border border-red-500/60 hover:border-red-400 hover:bg-red-500/10 text-red-400 font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 transition-colors"
            >
                <StopCircle className="w-3 h-3" /> Stop
            </button>
        );
    }

    const accentText = accent === "emerald" ? "text-emerald-400 hover:bg-emerald-400" : "text-amber-400 hover:bg-amber-400";
    const accentBorder = accent === "emerald" ? "border-emerald-500/60 hover:border-emerald-400" : "border-amber-500/60 hover:border-amber-400";

    return (
        <div className="ml-auto flex items-center gap-2" data-testid={`batch-${section}-controls`}>
            <div className="flex items-center border border-zinc-800">
                <button
                    type="button"
                    onClick={() => setN((v) => Math.max(1, v - 1))}
                    aria-label="Decrease N"
                    data-testid={`batch-${section}-dec`}
                    className="px-1.5 py-0.5 font-mono text-xs text-zinc-400 hover:text-amber-500 hover:bg-zinc-900 transition-colors"
                >−</button>
                <span className="font-mono text-[11px] text-zinc-200 px-2 min-w-[28px] text-center" data-testid={`batch-${section}-n`}>{n}</span>
                <button
                    type="button"
                    onClick={() => setN((v) => Math.min(cap, v + 1))}
                    aria-label="Increase N"
                    data-testid={`batch-${section}-inc`}
                    className="px-1.5 py-0.5 font-mono text-xs text-zinc-400 hover:text-amber-500 hover:bg-zinc-900 transition-colors"
                >+</button>
            </div>
            <button
                type="button"
                onClick={() => onRun(n)}
                data-testid={`batch-${section}-run`}
                title={`Run top ${n} sequentially · ~${n * costPerRow} AI requests total`}
                className={`inline-flex items-center gap-1.5 border ${accentBorder} ${accentText} hover:text-black font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 transition-colors`}
            >
                <Zap className="w-3 h-3" /> Run top {n}
                <span className="text-zinc-500">· ~{n * costPerRow}<small className="ml-0.5">AI</small></span>
            </button>
        </div>
    );
}

// Live progress strip — shows during a batch run.
function BatchProgress({ state, accent, testid }) {
    const pct = state.total > 0 ? Math.round((state.done / state.total) * 100) : 0;
    const barColor = accent === "emerald" ? "bg-emerald-500" : "bg-amber-500";
    const textColor = accent === "emerald" ? "text-emerald-400" : "text-amber-400";
    // Clamp the "current position" so we never read 'Running 3/2' at the loop tail.
    const currentPos = Math.min(state.done + 1, state.total);
    return (
        <div className="mb-3 border border-zinc-800 bg-black p-2.5" data-testid={testid}>
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider mb-1.5">
                <span className={textColor}>
                    Running {currentPos}/{state.total}
                    {state.current && <span className="text-zinc-500"> · {state.current}</span>}
                </span>
                <span className="text-zinc-500">{pct}%</span>
            </div>
            <div className="h-1 bg-zinc-900">
                <div className={`h-1 ${barColor} transition-all duration-300`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

